import { Mail, Phone, Globe, Building2, FileText } from 'lucide-react';
import type { Client } from '@core/types';

function initials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

/**
 * Mirrors CandidateProfileSummary — renders as ggj-profile-section-h + ggj-profile-section-body
 * to sit directly inside the Overview tab's ggj-param-group container.
 */
export function ClientProfileSummary({
  client,
  jobCount,
  appCount,
}: {
  client: Client;
  jobCount: number;
  appCount: number;
}) {
  const domainHref = client.domain
    ? client.domain.startsWith('http') ? client.domain : `https://${client.domain}`
    : null;

  return (
    <>
      <div className="ggj-profile-section-h">At a glance</div>
      <div className="ggj-profile-section-body">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Avatar + quick actions */}
          <div className="flex items-start gap-3 sm:gap-4">
            <div
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center text-xl sm:text-2xl font-bold shrink-0"
              style={{
                background: 'var(--brand-accent-bg)',
                border: '1px solid var(--brand-accent-border)',
                color: 'var(--vscode-primary)',
              }}
              aria-hidden
            >
              <Building2 className="w-10 h-10" style={{ color: 'var(--vscode-primary)' }} />
            </div>

            <div className="min-w-0 flex-1 space-y-2">
              {/* Industry badge */}
              {client.industry && (
                <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">
                  {client.industry}
                </p>
              )}

              {/* Quick contact links */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1">
                  Quick contact
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {client.contact_email ? (
                    <a
                      href={`mailto:${client.contact_email}`}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-[var(--vscode-border-primary)] vscode-text-primary hover:bg-[var(--vscode-bg-hover)] transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5 shrink-0 text-sky-600 dark:text-sky-400" />
                      Email
                    </a>
                  ) : null}
                  {client.contact_phone ? (
                    <a
                      href={`tel:${client.contact_phone.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-[var(--vscode-border-primary)] vscode-text-primary hover:bg-[var(--vscode-bg-hover)] transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      Call
                    </a>
                  ) : null}
                  {domainHref ? (
                    <a
                      href={domainHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-[var(--vscode-border-primary)] text-[#0a66c2] dark:text-[#70b7ff] hover:bg-[var(--vscode-bg-hover)] transition-colors"
                    >
                      <Globe className="w-3.5 h-3.5 shrink-0" />
                      Website
                    </a>
                  ) : null}
                </div>
              </div>

              {/* Pipeline snapshot */}
              <div className="flex gap-4 pt-1">
                <div>
                  <p className="text-[11px] uppercase tracking-wider vscode-text-tertiary">Open jobs</p>
                  <p className="text-lg font-bold vscode-text-primary tabular-nums">{jobCount}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider vscode-text-tertiary">Applications</p>
                  <p className="text-lg font-bold vscode-text-primary tabular-nums">{appCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: description / notes */}
          <div className="flex-1 min-w-0 space-y-3 lg:border-l lg:border-[var(--vscode-border-primary)] lg:pl-5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-0.5 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> About
              </p>
              <p className="text-sm vscode-text-primary leading-snug whitespace-pre-wrap">
                {client.notes?.trim() ? client.notes : (
                  <span className="vscode-text-tertiary">No description yet.</span>
                )}
              </p>
            </div>

            {/* Commercial agreement quick info */}
            {(client.commercial_agreement_label || client.contract_start_date || client.contract_end_date) && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary mb-1">
                  Agreement
                </p>
                <div className="space-y-0.5 text-sm">
                  {client.commercial_agreement_label && (
                    <p className="vscode-text-primary">{client.commercial_agreement_label}</p>
                  )}
                  {client.contract_start_date && (
                    <p className="vscode-text-secondary text-xs">
                      {client.contract_start_date}
                      {client.contract_end_date ? ` → ${client.contract_end_date}` : ''}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
